import { useEffect, useRef, useState } from 'react';
import { caseStudies } from '@/mocks/case-studies';

function useScrollReveal(threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, visible };
}

export default function CaseStudiesSection() {
  const headerReveal = useScrollReveal();
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <section className="py-16 md:py-24 border-t border-white/5">
      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">

        {/* Header */}
        <div
          ref={headerReveal.ref}
          className={`mb-10 md:mb-14 transition-all duration-700 ${
            headerReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3">
            Client Results
          </p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 lg:gap-4">
            <h2 className="text-white text-xl sm:text-2xl lg:text-3xl font-bold leading-tight">
              Engagements That Delivered Measurable Outcomes
            </h2>
            <p className="text-white/40 text-sm leading-relaxed max-w-md">
              Real organizations. Real operational challenges. Documented results across executive search, digital infrastructure, and workforce strategy.
            </p>
          </div>
        </div>

        {/* Case Study Cards */}
        <div className="space-y-6">
          {caseStudies.map((study, i) => {
            const isOpen = expanded === study.id;
            return (
              <div
                key={study.id}
                className={`bg-dark-card rounded-2xl border transition-all duration-500 overflow-hidden ${
                  isOpen ? 'border-gold/25' : 'border-white/5 hover:border-white/10'
                } ${
                  headerReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${200 + i * 120}ms` }}
              >
                {/* Card Header — always visible */}
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : study.id)}
                  className="w-full text-left p-6 md:p-8 cursor-pointer"
                  aria-expanded={isOpen}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                    {/* Icon */}
                    <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-xl shrink-0">
                      <i className={`${study.icon} text-gold text-xl`}></i>
                    </div>

                    {/* Main info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
                        <h3 className="text-white font-bold text-lg md:text-xl">{study.client}</h3>
                        <span className="text-white/30 text-xs font-medium bg-white/5 px-2.5 py-1 rounded-full whitespace-nowrap">
                          {study.industry}
                        </span>
                      </div>
                      <p className="text-gold text-sm font-medium mb-3">{study.engagement}</p>

                      {/* Outcome metrics — always visible */}
                      <div className="flex flex-wrap gap-4 md:gap-6">
                        {study.outcomes.map((outcome) => (
                          <div key={outcome.label} className="flex items-center gap-2">
                            <span className="text-white font-extrabold text-lg md:text-xl tracking-tight">
                              {outcome.metric}
                            </span>
                            <span className="text-white/40 text-xs leading-tight max-w-[140px]">
                              {outcome.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Expand toggle */}
                    <div className="flex items-center gap-2 shrink-0 self-start mt-1">
                      <span className="text-white/30 text-xs font-medium hidden sm:inline">
                        {isOpen ? 'Less' : 'Details'}
                      </span>
                      <div className={`w-7 h-7 flex items-center justify-center rounded-full border border-white/10 text-white/40 transition-all duration-300 ${isOpen ? 'rotate-180 border-gold/30 text-gold' : ''}`}>
                        <i className="ri-arrow-down-s-line text-sm"></i>
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mt-4 sm:mt-3 sm:ml-[68px]">
                    {study.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-white/30 text-[10px] font-medium border border-white/8 px-2.5 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </button>

                {/* Expanded Detail */}
                {isOpen && (
                  <div className="px-4 sm:px-6 md:px-8 pb-6 sm:pb-8 border-t border-white/5">
                    <div className="pt-5 sm:pt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">

                      {/* Left: Challenge + Implementation */}
                      <div className="space-y-6">
                        <div>
                          <p className="text-gold text-[10px] font-semibold tracking-[0.2em] uppercase mb-3">
                            Operational Challenge
                          </p>
                          <p className="text-white/55 text-sm leading-relaxed">
                            {study.challenge}
                          </p>
                        </div>

                        <div>
                          <p className="text-gold text-[10px] font-semibold tracking-[0.2em] uppercase mb-3">
                            Implementation
                          </p>
                          <ul className="space-y-2.5">
                            {study.implementation.map((item) => (
                              <li key={item} className="flex items-start gap-3">
                                <span className="w-4 h-4 flex items-center justify-center rounded-full border border-gold/30 mt-0.5 shrink-0">
                                  <i className="ri-check-line text-gold text-[9px]"></i>
                                </span>
                                <span className="text-white/55 text-sm leading-relaxed">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Right: Outcomes + Long-term Impact */}
                      <div className="space-y-6">
                        <div>
                          <p className="text-gold text-[10px] font-semibold tracking-[0.2em] uppercase mb-3">
                            Business Outcomes
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {study.outcomes.map((outcome) => (
                              <div
                                key={outcome.label}
                                className="bg-dark-surface rounded-xl p-4 border border-white/5"
                              >
                                <p className="text-white font-extrabold text-2xl md:text-3xl tracking-tight mb-1">
                                  {outcome.metric}
                                </p>
                                <p className="text-white/40 text-xs leading-relaxed">
                                  {outcome.label}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div>
                          <p className="text-gold text-[10px] font-semibold tracking-[0.2em] uppercase mb-3">
                            Long-Term Impact
                          </p>
                          <p className="text-white/55 text-sm leading-relaxed">
                            {study.longTermImpact}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom note */}
        <div
          className={`mt-10 flex items-center gap-4 transition-all duration-700 delay-500 ${
            headerReveal.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="w-8 h-px bg-gold/30"></div>
          <p className="text-white/25 text-xs">
            Outcomes reported by clients. Results vary by engagement scope and organizational context.
          </p>
        </div>
      </div>
    </section>
  );
}